import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldAlert, X, ShieldCheck } from 'lucide-react';

const SecurityPopup: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  // Este candado evita que el scroll lo vuelva a abrir después de cerrar
  const isClosedSession = useRef(false);

  useEffect(() => {
    // Si ya existe en la sesión al cargar, activamos el candado de una vez
    if (sessionStorage.getItem('hasSeenPopup')) {
      isClosedSession.current = true;
      return;
    }

    const handleScroll = () => {
      // SI EL CANDADO ESTÁ ACTIVADO, NO HACER NADA
      if (isClosedSession.current) return;

      const contactSection = document.getElementById('contacto') || document.getElementById('contact');

      if (contactSection) {
        const rect = contactSection.getBoundingClientRect();
        if (rect.top <= window.innerHeight * 0.8) {
          setIsOpen(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closePopup = () => {
    setIsOpen(false);
    // ACTIVAMOS EL CANDADO INSTANTÁNEAMENTE
    isClosedSession.current = true;
    sessionStorage.setItem('hasSeenPopup', 'true');
  };

  const handleWhatsAppRedirect = () => {
    const phoneNumber = "584141967028";
    const message = encodeURIComponent("Hola Stellionet, me gustaría solicitar el Diagnóstico de Seguridad Inicial gratuito para mi empresa.");
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  const banner = (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed top-0 left-0 w-full z-[10000] px-4 py-4 md:py-6 pointer-events-none">
          <motion.div
            initial={{ y: -200, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -200, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 120 }}
            className="relative w-full max-w-6xl mx-auto bg-white rounded-[2rem] shadow-[0_40px_100px_-15px_rgba(0,0,0,0.35)] border border-primary/10 overflow-hidden pointer-events-auto"
          >
            {/* Imagen de fondo decorativa */}
            <div className="absolute right-0 top-0 h-full w-1/3 opacity-[0.07] pointer-events-none hidden md:block">
              <ShieldCheck size={400} className="text-primary translate-x-20 -translate-y-10" />
            </div>

            {/* Botón Cerrar */}
            <button
              onClick={closePopup}
              className="absolute top-4 right-6 p-2 text-primary/60 hover:text-primary active:scale-95 transition-all z-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-full bg-white/10 hover:bg-white/20"
              aria-label="Cerrar ventana emergente"
              title="Cerrar"
            >
              <X size={24} aria-hidden="true" />
            </button>

            <div className="flex flex-col md:flex-row items-center">
              <div className="bg-secondary/10 rounded-2xl mx-4 p-6 md:p-5 flex items-center justify-center md:w-auto w-full">
                <div className="relative">
                  <ShieldAlert className="text-secondary animate-pulse" size={48} />
                  <div className="absolute inset-0 bg-secondary/20 blur-xl rounded-full" />
                </div>
              </div>

              <div className="p-6 md:p-10 flex-grow text-center md:text-left">
                <span className="inline-block px-3 py-1 bg-secondary/10 text-secondary text-[10px] font-bold uppercase tracking-widest rounded-full mb-3">
                  Alerta de Ciberseguridad
                </span>
                <h3 className="text-xl md:text-3xl font-display font-black text-primary tracking-tighter leading-tight mb-2">
                  ¿Tu empresa es vulnerable a un ataque de <span className="text-secondary">Ransomware?</span>
                </h3>
                <p className="text-primary/60 text-sm md:text-base font-medium max-w-2xl">
                  Obtén un <strong>Diagnóstico de Seguridad Inicial</strong> sin costo. Evaluamos tus antivirus y perímetros de red.
                </p>
              </div>

              <div className="p-6 md:p-10 md:pl-0 w-full md:w-auto">
                <button
                  className="btn-primary-hero inline-flex items-center gap-3 transition-all px-8 py-4 text-base font-bold shadow-lg focus:ring-4 focus:ring-secondary/50 outline-none"
                  onClick={handleWhatsAppRedirect}
                  aria-label="Solicitar una auditoría gratuita a través de WhatsApp"
                >
                  Solicitar Auditoría Gratuita
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return createPortal(banner, document.body);
};

export default SecurityPopup;